import { skipToken } from '@reduxjs/toolkit/query'
import { useLocale, useTranslations } from 'next-intl'
import { useCallback, useMemo, useState } from 'react'

import { useGetAircraftAmenitiesQuery } from '@/src/api/rtkQuery/srpFlightDetailsApi'
import { AircraftAmenitiesRequest, AircraftAmenityValue } from '@/src/types/flight'

import AircraftAmenities, { type TooltipAmenityItem } from './AircraftAmenities'

interface AircraftAmenitiesWrapperProps {
    airlineCode: string
    aircraftCode?: string
    flightClass: string
    showKnowMore?: boolean
}

const CABIN_CODE_MAP: Record<string, string> = {
    ECONOMY: 'Y',
    BUSINESS: 'B',
    FIRST: 'F'
}

const AMENITY_CHILDREN_MAP: Record<string, string[]> = {
    seatType: ['seatPitch', 'seatWidth', 'seatLength'],
    avod: ['avodScreenSize', 'videoType'],
    seatLayout: ['noOfSeats'],
    powerType: ['laptopPower']
}

const CHILD_I18N_MAP: Record<string, { messageKey: string; paramName: string }> = {
    seatPitch: { messageKey: 'aircraftAmenitiesSeatPitch', paramName: 'pitch' },
    seatWidth: { messageKey: 'aircraftAmenitiesSeatWidth', paramName: 'width' },
    seatLength: { messageKey: 'aircraftAmenitiesSeatLength', paramName: 'length' }
}

const resolveAmenityParam = (amenity?: AircraftAmenityValue): string =>
    String(amenity?.displayText ?? amenity?.value ?? '')

const ICON_AMENITY_KEYS = [
    'seatType',
    'seatLayout',
    'meals',
    'beverages',
    'wifi',
    'powerType',
    'avod',
    'infantCare',
    'mobile',
    'prayerArea',
    'provideTransportation',
    'showerArea'
]

const isAmenityActive = (value: string | number | boolean | null | undefined): boolean => {
    if (value === null || value === undefined) return false
    if (typeof value === 'boolean') return value
    if (typeof value === 'number') return value > 0
    if (typeof value === 'string') {
        const normalizedValue = value.trim().toLowerCase()
        return normalizedValue.length > 0 && normalizedValue !== 'false'
    }
    return false
}

const AircraftAmenitiesWrapper = ({
    airlineCode,
    aircraftCode,
    flightClass,
    showKnowMore = false
}: AircraftAmenitiesWrapperProps) => {
    const locale = useLocale()
    const t = useTranslations('srpFlightDetails')
    const [mobileDetailsOpen, setMobileDetailsOpen] = useState(false)
    const openMobileDetails = useCallback(() => setMobileDetailsOpen(true), [])
    const closeMobileDetails = useCallback(() => setMobileDetailsOpen(false), [])

    const cabinCode = useMemo(() => CABIN_CODE_MAP[flightClass?.toUpperCase()] ?? null, [flightClass])

    const queryArg = useMemo<AircraftAmenitiesRequest | typeof skipToken>(() => {
        if (!airlineCode || !aircraftCode || !cabinCode) return skipToken
        return { airlineCode, aircraftCode, lang: locale }
    }, [airlineCode, aircraftCode, cabinCode])

    const { data, isLoading, isError } = useGetAircraftAmenitiesQuery(queryArg)

    const matchedAircraftAmenities = useMemo(() => {
        if (!data || !aircraftCode) return null
        return data.find(item => item.aircraftCode === aircraftCode && item.airlineCode === airlineCode) ?? data[0] ?? null
    }, [aircraftCode, airlineCode, data])

    const cabinAmenities = useMemo(() => {
        if (!matchedAircraftAmenities || !cabinCode) return null
        return matchedAircraftAmenities.amenities?.[cabinCode] ?? null
    }, [cabinCode, matchedAircraftAmenities])

    const cabinImages = useMemo(() => {
        if (!matchedAircraftAmenities || !cabinCode) return null
        return matchedAircraftAmenities.images?.[cabinCode] ?? null
    }, [cabinCode, matchedAircraftAmenities])

    const activeAmenityKeys = useMemo(() => {
        if (!cabinAmenities) return []
        return ICON_AMENITY_KEYS.filter(key => isAmenityActive(cabinAmenities[key]?.value))
    }, [cabinAmenities])

    const tooltipItems = useMemo<TooltipAmenityItem[]>(() => {
        if (!cabinAmenities) return []

        const childDisplayText = (parentKey: string, childKey: string): string => {
            const amenity = cabinAmenities[childKey]
            if (parentKey === 'seatLayout') {
                return t('aircraftAmenitiesNumberOfSeats', { seats: resolveAmenityParam(amenity) })
            }
            const i18n = CHILD_I18N_MAP[childKey]
            if (i18n) return t(i18n.messageKey, { [i18n.paramName]: resolveAmenityParam(amenity) })
            return amenity?.displayText ?? ''
        }

        const parentDisplayText = (key: string): string => {
            const amenity = cabinAmenities[key]
            if (key === 'seatLayout') {
                return `${amenity?.displayText ?? ''} : ${String(amenity?.value ?? '')}`
            }
            return amenity?.displayText ?? ''
        }

        return activeAmenityKeys.map(key => ({
            key,
            displayText: parentDisplayText(key),
            children: (AMENITY_CHILDREN_MAP[key] ?? [])
                .filter(childKey => isAmenityActive(cabinAmenities[childKey]?.value))
                .map(childKey => ({
                    key: childKey,
                    displayText: childDisplayText(key, childKey)
                }))
        }))
    }, [activeAmenityKeys, cabinAmenities, t])

    if (isLoading || isError || activeAmenityKeys.length === 0) return null

    void cabinImages

    return (
        <AircraftAmenities
            activeAmenityKeys={activeAmenityKeys}
            tooltipItems={tooltipItems}
            amenitiesRemarksNote={cabinAmenities?.remarks?.displayText || undefined}
            showKnowMore={showKnowMore}
            mobileDetailsOpen={mobileDetailsOpen}
            onKnowMoreClick={openMobileDetails}
            onMobileDetailsClose={closeMobileDetails}
        />
    )
}

export default AircraftAmenitiesWrapper
